import React, { memo, useCallback, useRef, RefObject, useEffect, useMemo } from "react";
import { ReactElement } from "react";

//components
import { useFocusDialog, getActiveElement, AriaHidden, useFocusTrap, useEffectOnce } from "@swiggy-private/core-ui";
import {
    AddNewAddressButton,
    AddNewAddressButtonIcon,
    AddressListContentContainer,
    AddressListHeader,
} from "./components/styles";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as AddIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/plus/16";
import { Address } from "./components/Address";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";

// utils
import { getPositionFromBottomForAddressList } from "@cart-ui/helpers/CartFooter";

//types and constants
import { AddressInfo } from "@swiggy-private/cart-types";
import { SELECT_ADDRESS_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { ADDRESS_LIST_SOURCE } from "@cart-ui/constants/Address";

export interface AddressListV2Props {
    addresses: AddressInfo[];
    addNewAddress: ({
        selectedAddress,
        addressList,
        source,
    }: {
        selectedAddress?: AddressInfo | null;
        addressList?: AddressInfo[];
        source?: string;
    }) => void;
    selectAddress: (selectedAddress: AddressInfo, addressList: AddressInfo[]) => void;
    hideOnOverlayClick?: boolean;
    onClose?: VoidFunction;
    showAddressNudgeIcon: boolean;
    onImpression: (selectedAddress: AddressInfo | null, addressList: AddressInfo[]) => void;
    showAddressList: boolean;
}

type AddressListContentProps = Omit<AddressListV2Props, "hideOnOverlayClick" | "onClose" | "showAddressList">;

const TEXTS = {
    CHOOSE_DELIVERY_ADDRESS: "Choose a delivery address",
    ADD_NEW_ADDRESS: "Add new Address",
};

const AddressListContent = React.memo(function AddressListContent(props: AddressListContentProps): React.ReactElement {
    useFocusTrap();
    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);

    const selectedAddress = useMemo(
        () => (props?.addresses?.[0]?.isSelected ? props?.addresses?.[0] : null),
        [props?.addresses],
    );

    const handleAddNewAddressClick = useCallback(
        () =>
            props.addNewAddress({
                selectedAddress,
                addressList: props.addresses,
                source: ADDRESS_LIST_SOURCE.ADDRESS_LIST_V2,
            }),
        [props, selectedAddress],
    );

    useEffectOnce(() => {
        previouslyFocusedElement.current = getActiveElement();
    });

    useEffect(() => {
        props.onImpression(selectedAddress, props?.addresses);
    }, [props, selectedAddress]);

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    return (
        <>
            <AddressListHeader
                variant="V2.subheading1.neutral.medium"
                color="text_Highest_Emphasis"
                ref={headerDivRef as RefObject<HTMLDivElement>}
                role="heading"
                aria-level={3}
                tabIndex={1}
            >
                {TEXTS.CHOOSE_DELIVERY_ADDRESS}
            </AddressListHeader>
            <AddressListContentContainer data-testid={SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST}>
                <AddNewAddressButton
                    onClick={handleAddNewAddressClick}
                    aria-label={TEXTS.ADD_NEW_ADDRESS}
                    data-testid={SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST_ADD_NEW_ADDRESS}
                >
                    <AddNewAddressButtonIcon as="div">
                        <GenericIcon Icon={AddIcon} fillColor="primary" strokeColor="primary" />
                    </AddNewAddressButtonIcon>
                    <AriaHidden as="div">
                        <Text variant="V2.body1.neutral.bold" color="primary">
                            {TEXTS.ADD_NEW_ADDRESS}
                        </Text>
                    </AriaHidden>
                </AddNewAddressButton>
                {props.addresses.map((address) => (
                    <Address
                        address={address}
                        selectAddress={props.selectAddress}
                        key={address.id}
                        addressList={props.addresses}
                        showAddressNudgeIcon={props.showAddressNudgeIcon}
                    />
                ))}
            </AddressListContentContainer>
        </>
    );
});

function AddressList(props: AddressListV2Props): ReactElement {
    const { showAddressList, hideOnOverlayClick = true, onClose, ...addressListContentProps } = props;
    return (
        <HalfCard
            isOpen={showAddressList}
            hasCustomPadding
            closeOnOverlayClick={hideOnOverlayClick}
            onClose={onClose}
            positionFromBottom={getPositionFromBottomForAddressList()}
        >
            <AddressListContent {...addressListContentProps} />
        </HalfCard>
    );
}

export default memo(AddressList);
